import { Box, Grid } from '@mui/material'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { getAssignmentValidationSchema, getInitialValues } from './helpers'
import { makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { useNotification } from 'hooks'
import { handleQueryError } from 'utils/helpers'
import { useDelegatesQuery, useManagersQuery } from 'components/iam/partners/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { Button, IconButton } from '@fullscreendigital/carrefour-dex-fe'
import { GroupsOutlined, Person } from '@mui/icons-material'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import EditManagerModal from 'components/iam/partners/Managers/EditManager/EditManagerModal'
import EditDelegateModal from 'components/iam/partners/Delegates/EditDelegate/EditDelegateModal'
import { toAssignmentManagementPayload } from './serializers'

const EditManagementDetailsModal = props => {
  const { isModalOpen, onModalClose, assignment, refetchAssignment } = props

  const [managersOptions, setManagersOptions] = useState([])
  const [delegatesOptions, setDelegatesOptions] = useState([])
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false)
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState(false)

  const openManagerModal = () => setIsManagerModalOpen(true)
  const closeManagerModal = () => setIsManagerModalOpen(false)

  const openDelegateModal = () => setIsDelegateModalOpen(true)
  const closeDelegateModal = () => setIsDelegateModalOpen(false)

  const { showNotification } = useNotification()

  const {
    data: managersData,
    isLoading: isManagersLoading,
    refetch: refetchManagers
  } = useManagersQuery(showNotification, '/iam/external-partners/summaries/managers-slim', {
    enabled: isModalOpen
  })

  useEffect(() => {
    if (managersData) {
      setManagersOptions(
        managersData.map(manager => ({
          label: manager.fullName,
          value: manager.id
        }))
      )
    }
  }, [managersData])

  const {
    data: delegatesData,
    isLoading: isDelegatesLoading,
    refetch: refetchDelegates
  } = useDelegatesQuery(
    showNotification,
    toTableGETUrl({
      page: 0,
      rowsPerPage: 1000,
      url: '/iam/external-partners/delegates',
      tableFilters: [{ name: 'isEnabled', value: true }]
    }),
    { enabled: isModalOpen }
  )

  useEffect(() => {
    if (delegatesData) {
      setDelegatesOptions(
        delegatesData.list.map(delegate => ({
          label: `${delegate.firstName} ${delegate.lastName}`,
          value: delegate.id
        }))
      )
    }
  }, [delegatesData])

  const initialValues = useMemo(() => getInitialValues(assignment), [assignment])

  const validationSchema = useMemo(() => getAssignmentValidationSchema(), [])

  const mutateAssignment = async values => {
    await makeCorePutRequest(`/iam/external-partners/assignments/${assignment.id}`, values)
  }

  const { isLoading, mutate } = useMutation(mutateAssignment, {
    onSuccess: res => {
      refetchAssignment && refetchAssignment()
      closeModal()
      formik.resetForm()
      showNotification({
        message: 'Assignment management details updated successfully.',
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(error, 'Failed to update assignment management.', showNotification)
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(toAssignmentManagementPayload(values, assignment))
    }
  })

  const closeModal = () => {
    onModalClose()
    formik.resetForm()
  }

  return (
    <>
      <EditManagerModal
        isModalOpen={isManagerModalOpen}
        onModalClose={closeManagerModal}
        onSuccess={() => {}}
        selectManager={manager =>
          formik.setFieldValue('manager', {
            label: `${manager.firstName} ${manager.lastName}`,
            value: manager.id
          })
        }
      />
      <EditDelegateModal
        isModalOpen={isDelegateModalOpen}
        onModalClose={closeDelegateModal}
        onSuccess={() => {}}
        selectDelegate={delegate =>
          formik.setFieldValue('delegate', {
            label: `${delegate.firstName} ${delegate.lastName}`,
            value: delegate.id
          })
        }
      />
      <Modal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        modalTitle="Edit Assignment Management"
        modalWidth="50%"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <AsyncAutocomplete
                  variant="outlined"
                  name="status"
                  label="Manager"
                  value={formik.values.manager}
                  options={managersOptions}
                  onChange={value => {
                    formik.setFieldValue('manager', value)
                    formik.setFieldValue('delegate', null)
                  }}
                  required
                  error={!!formik.errors.manager}
                  helperText={formik.errors.manager as string}
                />
              </Grid>
              <Grid item xs={4}>
                <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                  <IconButton
                    endIcon={<Person />}
                    variant="outlined"
                    label="New manager"
                    onClick={() => openManagerModal()}
                    style={{ flex: 'none' }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <AsyncAutocomplete
                  variant="outlined"
                  name="status"
                  label="Delegate"
                  value={formik.values.delegate}
                  options={delegatesOptions}
                  onChange={value => formik.setFieldValue('delegate', value)}
                  error={!!formik.errors.delegate}
                  helperText={formik.errors.delegate as string}
                />
              </Grid>
              <Grid item xs={4}>
                <Box style={{ marginTop: '20px', textAlign: 'right' }}>
                  <IconButton
                    endIcon={<GroupsOutlined />}
                    variant="outlined"
                    label="New delegate"
                    onClick={() => openDelegateModal()}
                    style={{ flex: 'none' }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} className={s.modalButtons}>
            <Button variant="outlined" label="Cancel" onClick={closeModal} />
            <Button
              variant="contained"
              label="Save"
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

export default EditManagementDetailsModal
